import React, {useCallback, useState} from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import ScrollBar from "../ui/ScrollBar";
import Loading from "../ui/Loading";
import EnhancedTableHead from "./EnhancedTableHead";
import EnhancedTableToolbar from "./EnhancedTableToolbar";
import {FilterValue, IDashboardBoxDataTableData, TableColumnsWithFilter} from "./_interfaces";
import DataTableRow from "./DataTableRow";
import FilterPopover from "./FilterPopover";

const EnhancedTable = (props: IDashboardBoxDataTableData) => {
    const {
        title = "",
        columns,
        rows,
        page,
        rowsPerPage = 0,
        totalRows = 0,
        onPageChange = () => {},
        handleRequestSort = () => {},
        order = "asc",
        orderBy = "",
        pagination = true,
        selected = [],
        setSelectedHandler = () => {},
        hasSelect = false,
        selectOptions = "",
        hasFilters = false,
        loading = false,
        onChangeFilters,
        onCancelFilters,
        onApplyFilters,
        resetFormData,
        activeColumnIds = [],
        onActiveColumnIdsChange,
        filterValues = {},
        filterOptions = {},
        hasExport = false,
        exportData,
        hasSearch = false,
        onSearch,
        onChange,
        searchValue = "",
        onClearSearch,
    } = props;

    const [dense, setDense] = useState(false);
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const tableColSpan = columns.length + (hasSelect ? 1 : 0);

    const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
    const [activeFilterColumn, setActiveFilterColumn] = useState<TableColumnsWithFilter | null>(null);
    const [globalFilterAnchorEl, setGlobalFilterAnchorEl] = useState<HTMLElement | null>(null);
    const isFilterOpen = Boolean(filterAnchorEl);

    const handleOpenFilter = useCallback((event: React.MouseEvent<HTMLElement>, column: TableColumnsWithFilter) => {
        setActiveFilterColumn(column);
        setFilterAnchorEl(event.currentTarget);
    }, [setActiveFilterColumn, setFilterAnchorEl]);

    const handleOpenGlobalFilter = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setGlobalFilterAnchorEl(event.currentTarget);
    }, [setGlobalFilterAnchorEl]);

    const handleCloseFilter = () => {
        setFilterAnchorEl(null);
        setActiveFilterColumn(null);
    };

    const handleCloseGlobalFilter = () => {
        setGlobalFilterAnchorEl(null);
    };

    const handleFilterChange = (filterType: string, columnId: string, value: FilterValue) => {
        onChangeFilters?.(filterType, columnId, value);
    };

    const handleCancelGlobalFilters = () => {
        handleCloseGlobalFilter();
        onCancelFilters?.();
    };

    const handleApplyFilters = () => {
        onApplyFilters?.();
    };

    const handleResetFilters = () => {
        resetFormData?.();
    };

    const handleActiveColumnIdsChange = (ids: string[]) => {
        onActiveColumnIdsChange?.(ids);
    };

    const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDense(event.target.checked);
    };

    const isSelected = (id: number) => selected.indexOf(id) !== -1;

    const handleSelectAllClick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.checked) {
            setSelectedHandler([]);
            return;
        }

        const newSelected = rows
            .filter((row) => row.disableSelect !== true)
            .map((n) => n.id);
        setSelectedHandler(newSelected);
        if (selected.length === newSelected.length) {
            setSelectedHandler([]);
        }
    }, [rows, setSelectedHandler, selected.length]);

    const handleClick = useCallback((_event: React.MouseEvent<unknown>, id: number) => {
        const newSelected = selected.includes(id)
            ? selected.filter((selectedId) => selectedId !== id)
            : [...selected, id];

        setSelectedHandler(newSelected);
    }, [selected, setSelectedHandler]);

    const denseSwitch = (
        <FormControlLabel
            control={<Switch checked={dense} onChange={handleChangeDense} />}
            label="Tabel restrâns"
        />
    );

    const renderTableBody = () => {
        if (loading) {
            return (
                <TableRow>
                    <TableCell colSpan={tableColSpan} align="left" width="100%">
                        <Box sx={{display: "flex", justifyContent: "center", width: "100%"}}>
                            <Loading show />
                        </Box>
                    </TableCell>
                </TableRow>
            );
        }

        if (rows.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={tableColSpan} align="left" width="100%">
                        Nicio înregistrare găsită.
                    </TableCell>
                </TableRow>
            );
        }

        return rows.map((row, index) => {
            const labelId = `enhanced-table-checkbox-${index}`;
            const isItemSelected = isSelected(row.id);
            const isRowSelectable = row.disableSelect !== true;

            return (
                <DataTableRow
                    key={row.id ?? row.test ?? index}
                    row={row}
                    columns={columns}
                    dense={dense}
                    hasSelect={hasSelect}
                    isMdUp={isMdUp}
                    isItemSelected={isItemSelected}
                    isRowSelectable={isRowSelectable}
                    labelId={labelId}
                    onRowClick={handleClick}
                />
            );
        });
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    // const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRows) : 0;

    return (
        <Box sx={{width: "100%"}}>
            <Box sx={{width: "100%", mb: {xs: 1, sm: 2}}}>
                {(hasFilters || hasSelect || hasExport || hasSearch) && (
                    <EnhancedTableToolbar
                        title={title}
                        numSelected={selected.length}
                        hasFilters={hasFilters}
                        hasSelect={hasSelect}
                        selectOptions={selectOptions}
                        onOpenFilter={handleOpenGlobalFilter}
                        activeFiltersNumber={activeColumnIds.length}
                        hasExport={hasExport}
                        exportData={exportData}
                        hasSearch={hasSearch}
                        onSearch={onSearch}
                        onChange={onChange}
                        searchValue={searchValue}
                        onClearSearch={onClearSearch}
                    />
                )}
                <TableContainer>
                    <ScrollBar forceVisible="x" autoHide={false}>
                        <Table
                            aria-labelledby="tableTitle"
                            size={dense ? "small" : "medium"}
                            sx={{marginBottom: "12px"}}
                        >
                            <EnhancedTableHead
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={totalRows}
                                columns={columns}
                                onSelectAllClick={handleSelectAllClick}
                                hasSelect={hasSelect}
                                dense={dense}
                                onOpenFilter={handleOpenFilter}
                                hasFilters={hasFilters}
                            />
                            <TableBody>{renderTableBody()}</TableBody>
                        </Table>
                    </ScrollBar>
                </TableContainer>

                <Box display="flex" justifyContent={{xs: "flex-end", md: "space-between"}}>
                    {isMdUp && denseSwitch}
                    {pagination && (
                        <TablePagination
                            rowsPerPageOptions={[15]}
                            component="div"
                            count={totalRows}
                            rowsPerPage={rowsPerPage}
                            labelDisplayedRows={function defaultLabelDisplayedRows({from, to, count}) {
                                return `${from}–${to} din ${count !== -1 ? count : `mai mult de ${to}`}`;
                            }}
                            page={page}
                            onPageChange={onPageChange}
                        />
                    )}
                </Box>
                {!isMdUp && denseSwitch}
            </Box>

            <FilterPopover
                columns={columns}
                globalFilterAnchorEl={globalFilterAnchorEl}
                handleCloseGlobalFilter={handleCloseGlobalFilter}
                filterAnchorEl={filterAnchorEl}
                isFilterOpen={isFilterOpen}
                handleCloseFilter={handleCloseFilter}
                activeFilterColumn={activeFilterColumn}
                filterValues={filterValues}
                handleFilterChange={handleFilterChange}
                handleCancelGlobalFilters={handleCancelGlobalFilters}
                handleApplyFilters={handleApplyFilters}
                handleResetFilters={handleResetFilters}
                activeColumnIds={activeColumnIds}
                handleActiveColumnIdsChange={handleActiveColumnIdsChange}
                filterOptions={filterOptions}
            />
        </Box>
    );
};

export default EnhancedTable;